import { useMutation } from "@apollo/client";
import {
  Anchor,
  Button,
  createStyles,
  Group,
  LoadingOverlay,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useInterval } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { rolePermissions } from "utiles/permissions";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CREATE_NEW_ACCOUNT,
  LOGIN,
  RESEND_OTP,
  VERIFY_PHONE,
} from "../apollo/mutuations";

const useStyles = createStyles((theme) => ({
  wrapper: {
    height: "100vh",
    backgroundSize: "cover",
    backgroundImage:
      "url(https://indian-retailer.s3.ap-south-1.amazonaws.com/s3fs-public/2020-08/B2B_0.jpg)",
  },
  form: {
    borderRight: `1px solid ${theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.colors.gray[3]}`,
    minHeight: "100vh",
    maxWidth: 450,
    paddingTop: 80,
    [`@media (max-width: ${theme.breakpoints.sm}px)`]: { maxWidth: "100%" },
  },
  title: {
    color: theme.colorScheme === "dark" ? theme.white : theme.black,
    fontFamily: `Greycliff CF, ${theme.fontFamily}`,
  },
}));

const AuthPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const recaptchaRef = useRef(null);

  const [type, setType] = useState("login");
  const [errors, setErrors] = useState({});
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");

  const [captchaToken, setCaptchaToken] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const [attempts, setAttempts] = useState(0);

  const interval = useInterval(() => setCooldown((s) => s - 1), 1000);

  useEffect(() => {
    if (cooldown > 0) interval.start();
    else interval.stop();
    return interval.stop;
  }, [cooldown]);
  const [loginReq, { loading: loginLoading }] = useMutation(LOGIN);
  const [registerReq, { loading: regLoading }] =
    useMutation(CREATE_NEW_ACCOUNT);
  const [verifyReq, { loading: verifyLoading }] = useMutation(VERIFY_PHONE);
  const [resendReq, { loading: resendLoading }] = useMutation(RESEND_OTP);

  const getDeviceFingerprint = () => {
    return btoa(window.navigator.userAgent + window.screen.width);
  };

  const validate = () => {
    let tempErrors = {};
    if (type === "register") {
      if (!phone.trim()) tempErrors.phone = "Required";
      else if (!/^\+?\d+$/.test(phone)) tempErrors.phone = "Invalid format";
      if (password !== confirmPassword) tempErrors.confirmPassword = "Mismatch";
      if (!name) tempErrors.name = "Required";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (type === "login") {
      handleLogin();
    } else if (type === "register") handleRegister();
    else handleVerify();
  };

  const handleLogin = () => {
    loginReq({
      variables: {
        email,
        password,
      },
      onCompleted(data) {
        const loginData = data.loginWithCaptcha || data.login;
        if (loginData?.token) {
          localStorage.setItem("auth_token", loginData.token);
          localStorage.setItem("auth_token_created_at", Date.now().toString());
          localStorage.setItem(
            "permissions",
            JSON.stringify(loginData.permissions),
          );
          localStorage.setItem("roles", JSON.stringify(loginData.roles));

          // Check if user is a loan provider and navigate accordingly
          const isLoanProvider = loginData.roles.includes("loan_provider");
          const isMaker = rolePermissions.hasMaker(loginData.roles);
          const isChecker = rolePermissions.hasChecker(loginData.roles);
          if (isMaker || isChecker) {
            navigate("/loan-applications");
          } else if (isLoanProvider) {
            navigate("/loan-provider-dashboard");
          } else {
            navigate("/");
          }
        }
      },
      onError: (err) => {
        showError(err, "Login Failed");
        setCaptchaToken(null);
        recaptchaRef.current?.reset();
      },
    });
  };

  const handleRegister = () => {
    registerReq({
      variables: {
        name,
        phone,
        email,
        password,
        password_confirmation: confirmPassword,
        role: "SUPPLIER",
      },
      onCompleted() {
        showNotification({
          color: "green",
          title: "OTP Sent",
          message: "Check your phone",
        });
        setType("verify");
        setCooldown(60);
      },
      onError: (err) => showError(err, "Registration Failed"),
    });
  };

  const handleVerify = () => {
    verifyReq({
      variables: { phone, otp, fingerprint: getDeviceFingerprint() },
      onCompleted(data) {
        showNotification({
          color: "green",
          title: "Verified",
          message: data.verifyOtp.message,
        });
        setType("login");
      },
      onError: (err) => {
        setAttempts((v) => v + 1);
        showError(err, "Verification Failed");
      },
    });
  };

  const handleResendOtp = () => {
    if (cooldown > 0) return;
    resendReq({
      variables: { phone, fingerprint: getDeviceFingerprint() },
      onCompleted: (data) => {
        showNotification({ color: "blue", message: data.resendOtp.message });
        setCooldown(60);
        setAttempts((v) => v + 1);
      },
      onError: (err) => showError(err, "Resend Failed"),
    });
  };

  const showError = (error, title) => {
    showNotification({
      color: "red",
      title: title,
      message: error?.graphQLErrors?.[0]?.message || "Security or Server Error",
    });
  };

  return (
    <div className={classes.wrapper}>
      <LoadingOverlay
        visible={loginLoading || regLoading || verifyLoading || resendLoading}
      />
      <Paper className={classes.form} radius={0} p={30}>
        <Title
          order={2}
          className={classes.title}
          align="center"
          mt="md"
          mb={30}
        >
          {type === "login"
            ? "Welcome back!"
            : type === "register"
              ? "Create Account"
              : "Verify Phone"}
        </Title>

        <form onSubmit={handleSubmit}>
          <Stack>
            {type === "register" && (
              <>
                <TextInput
                  label="Full Name"
                  required
                  value={name}
                  error={errors.name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextInput
                  label="Phone Number"
                  placeholder="09..."
                  required
                  value={phone}
                  error={errors.phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </>
            )}

            {type !== "verify" && (
              <TextInput
                label="Email address"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}

            {type === "verify" && (
              <TextInput
                label="Enter OTP"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                description={`Sent to ${phone}`}
              />
            )}

            {type !== "verify" && (
              <PasswordInput
                label="Password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}

            {type === "register" && (
              <PasswordInput
                label="Confirm Password"
                required
                value={confirmPassword}
                error={errors.confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            )}
          </Stack>

          <Button
            fullWidth
            mt="xl"
            size="md"
            type="submit"
            disabled={attempts > 5 && type === "verify"}
          >
            {type === "login"
              ? "Login"
              : type === "register"
                ? "Send OTP"
                : "Verify & Continue"}
          </Button>

          {type === "verify" && (
            <Group position="center" mt="md">
              <Text size="sm">Didn't get the code?</Text>
              <Anchor
                size="sm"
                component="button"
                type="button"
                disabled={cooldown > 0}
                onClick={handleResendOtp}
                sx={(theme) => ({
                  color:
                    cooldown > 0 ? theme.colors.gray[5] : theme.colors.blue[6],
                  cursor: cooldown > 0 ? "not-allowed" : "pointer",
                })}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
              </Anchor>
            </Group>
          )}

          <Text align="center" mt="md">
            <Anchor
              component="button"
              type="button"
              color="blue"
              onClick={() => {
                setType(type === "login" ? "register" : "login");
                setErrors({});
              }}
              size="sm"
            >
              {type === "login"
                ? "Don't have an account? Register"
                : "Back to Login"}
            </Anchor>
          </Text>
        </form>
      </Paper>
    </div>
  );
};

export default AuthPage;
